/**
 * The calendar's menus — Aaron, Oct 24: "right clicking on items should have
 * options, including rescheduling, editing, send notification, etc..", the
 * stop card's ⋯ that "doesn't do anything", and a ⋯ on each person under
 * *Calendars* (notes 1.5–1.7).
 *
 * One menu, the app's own (`components/context-menu.tsx`), opened at a point
 * — the pointer, a ⋯, the item itself for Shift+F10 or the context-menu key,
 * a finger held still on an iPad — and drawn above whatever opened it
 * (`data-overlay-layer`), so the stop card it hangs off does not take its
 * press for a click outside. The keyboard walks it: the first item has the
 * focus, ↑ ↓ Home End move it, Enter picks, Escape and Tab close.
 *
 * `itemMenu` is the one list of what an item offers, by kind — the
 * right-click menu and the stop card's ⋯ are the same list, so they cannot
 * drift.
 */
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { firstNameOf, type CalendarItem } from '@rowted/shared'
import { ContextMenu, type MenuItem } from '../../components/context-menu.js'

/**
 * Above whatever it hangs off — a floating card (40), the stop card's bottom
 * sheet on an iPad held upright (70) — and drawn there too (`z-[75]`): at 45
 * under a z-50 menu, the portrait sheet's ⋯ opened its menu *behind* the
 * sheet, so nothing on it could be tapped (Aaron, Oct 25, phone note 4).
 */
export const MENU_LAYER = 75

export interface MenuAt { x: number; y: number }

/** Where a menu opened from the keyboard goes: just inside the element's top left. */
export function menuAtElement(element: Element): MenuAt {
  const rect = element.getBoundingClientRect()
  return { x: rect.left + 8, y: rect.top + Math.min(rect.height, 24) }
}

/** Shift+F10 and the context-menu key open an item's menu, as they do in Outlook. */
export function isMenuKey(event: { key: string; shiftKey: boolean }): boolean {
  return event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey)
}

export function CalendarMenu({
  items, at, onClose, label,
}: { items: MenuItem[]; at: MenuAt; onClose: () => void; label: string }) {
  const box = useRef<HTMLDivElement>(null)
  /* What had the focus gets it back when the menu goes. */
  const before = useRef<Element | null>(typeof document === 'undefined' ? null : document.activeElement)
  useEffect(() => {
    const first = box.current?.querySelector<HTMLElement>('[role^="menuitem"]')
    first?.focus()
    const returnTo = before.current
    return () => { if (returnTo instanceof HTMLElement && returnTo.isConnected) returnTo.focus() }
  }, [])
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    /* The menu may walk its own items; a key it took is not walked twice. */
    if (event.defaultPrevented) return
    const entries = [...(box.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [])]
    const at = entries.indexOf(document.activeElement as HTMLElement)
    const go = (index: number) => { event.preventDefault(); entries[(index + entries.length) % entries.length]?.focus() }
    if (event.key === 'ArrowDown') go(at + 1)
    else if (event.key === 'ArrowUp') go(at < 0 ? entries.length - 1 : at - 1)
    else if (event.key === 'Home') go(0)
    else if (event.key === 'End') go(entries.length - 1)
    else if (event.key === 'Tab') { event.preventDefault(); onClose() }
    else if (event.key === 'Escape') { event.stopPropagation(); onClose() }
  }
  if (items.length === 0) return null
  return createPortal(
    <div ref={box} data-overlay-layer={MENU_LAYER} data-calendar-menu aria-label={label} onKeyDown={onKeyDown}
      className="relative z-[75]" onContextMenu={(event) => event.preventDefault()}>
      <ContextMenu items={items} x={at.x} y={at.y} onClose={onClose} />
    </div>,
    document.body,
  )
}

/**
 * What the menu can do — each by the path its own card or key already uses.
 * One that is not given is not offered: a screen off the calendar (All
 * activity's stop card) offers what it can do from there.
 */
export interface ItemMenuActions {
  open?: () => void
  edit?: () => void
  reschedule?: () => void
  /** Text or email the contact, through Activity's own New text and New email. */
  message?: (channel: 'sms' | 'email') => void
  tick?: (done: boolean) => void
  openJob?: (jobId: string) => void
  remove?: () => void
}

/** Who a stop is with, once its card has been read (`GET /activity/stops/:id`). */
export interface MenuContact {
  firstName: string
  name?: string
  phone: string | null
  email: string | null
  optedOut: boolean
}

/**
 * What an item offers, in Outlook's order — open it, edit it, move it; tell
 * the customer; tick it; its job; remove it — and nothing it cannot do: a
 * thing the viewer may not change offers what opening and reading do. Remove
 * asks before anything goes (*Just this one · Every one* for a repeat; a site
 * visit is cancelled from its job), so it reads *Remove…*.
 */
export function itemMenu(
  item: CalendarItem, actions: ItemMenuActions,
  options: { contact?: MenuContact | null; canSend?: boolean; done?: boolean } = {},
): MenuItem[] {
  const out: MenuItem[] = []
  const done = options.done ?? item.done
  const stop = item.kind === 'appointment' || item.kind === 'meeting'
  /* A rule between groups: open · tell · tick · remove. */
  const groups: string[] = []
  const add = (entry: Omit<MenuItem, 'separatorBefore'>, group: string) => {
    out.push({ ...entry, separatorBefore: out.length > 0 && groups[groups.length - 1] !== group })
    groups.push(group)
  }
  if (actions.open) add({ key: 'open', label: 'Open', onSelect: actions.open }, 'open')
  if (actions.edit && item.editable && stop) add({ key: 'edit', label: 'Edit', onSelect: actions.edit }, 'open')
  if (actions.reschedule && item.editable) add({ key: 'reschedule', label: 'Reschedule', onSelect: actions.reschedule }, 'open')
  const contact = options.contact
  if (stop && contact && actions.message && options.canSend !== false) {
    const send = actions.message
    const first = contact.firstName || firstNameOf(contact.name ?? '')
    if (contact.phone && !contact.optedOut) add({ key: 'text', label: `Text ${first}`, onSelect: () => send('sms') }, 'tell')
    if (contact.email) add({ key: 'email', label: `Email ${first}`, onSelect: () => send('email') }, 'tell')
  }
  if (actions.tick && item.editable && item.kind !== 'block') {
    const tick = actions.tick
    if (!done) add({ key: 'done', label: 'Mark done', onSelect: () => tick(true) }, 'tick')
    else if (item.kind === 'task') add({ key: 'undone', label: 'Mark not done', onSelect: () => tick(false) }, 'tick')
  }
  if (actions.openJob && item.jobId) {
    const openJob = actions.openJob
    add({ key: 'job', label: 'Open the job', onSelect: () => openJob(item.jobId!) }, 'tick')
  }
  if (actions.remove && item.editable) add({ key: 'remove', label: 'Remove…', tone: 'danger', onSelect: actions.remove }, 'remove')
  return out
}
